import type {
  SiteRuntimeNavigationItem,
  SiteRuntimeNavigationLeaf,
  SiteRuntimeNavigationNode,
  SiteRuntimeNavigationSurface,
  SiteRuntimeShell,
} from './types';

export function getSiteRuntimeNavigationBadge(
  node: SiteRuntimeNavigationNode,
  surface: SiteRuntimeNavigationSurface
) {
  return node.badge?.visibleOn.includes(surface) ? node.badge.value : undefined;
}

export function getSiteRuntimeNavigationIcon(
  node: SiteRuntimeNavigationNode,
  surface: SiteRuntimeNavigationSurface
) {
  return node.icon?.visibleOn.includes(surface) ? node.icon : undefined;
}

export function getSiteRuntimeNavigationTarget(
  node: SiteRuntimeNavigationItem | SiteRuntimeNavigationLeaf,
  surface: SiteRuntimeNavigationSurface
) {
  if (surface === 'sidebar' && 'surfaces' in node) {
    return node.surfaces.sidebar?.target ?? node.target;
  }
  return node.target;
}

export function buildWorkspaceToolBadgeLabels(params: {
  entries: Iterable<{
    toolId?: string | null;
    badgeLabel?: string | null;
  }>;
  source: string;
}) {
  const badgeLabels = new Map<string, string>();

  for (const entry of params.entries) {
    const toolId = entry.toolId?.trim() ?? '';
    const badgeLabel = entry.badgeLabel?.trim() ?? '';
    if (!toolId || !badgeLabel) continue;

    const existingBadgeLabel = badgeLabels.get(toolId);
    if (existingBadgeLabel && existingBadgeLabel !== badgeLabel) {
      throw new Error(
        `${params.source} has conflicting badgeLabel values for toolId: ${toolId}`
      );
    }
    badgeLabels.set(toolId, badgeLabel);
  }

  return badgeLabels;
}

export function getSiteRuntimeNavigationToolBadgeLabels(
  shell: SiteRuntimeShell,
  surface: SiteRuntimeNavigationSurface
) {
  return buildWorkspaceToolBadgeLabels({
    entries: shell.navigation.items.flatMap((item) => [
      {
        toolId: item.toolId,
        badgeLabel: getSiteRuntimeNavigationBadge(item, surface),
      },
      ...item.childGroups.flatMap((group) =>
        group.links.map((link) => ({
          toolId: link.toolId,
          badgeLabel: getSiteRuntimeNavigationBadge(link, surface),
        }))
      ),
    ]),
    source: 'Site Runtime navigation',
  });
}

export function getSiteRuntimeFooterLegalLinks(shell: SiteRuntimeShell) {
  return shell.navigation.items.flatMap((item) =>
    item.childGroups.flatMap((group) =>
      group.links.filter((link) => link.footerLegal)
    )
  );
}
